import React, { useState, useEffect } from 'react';
import { X, Phone, ShoppingBag, CheckCircle2, Tag, MessageSquare, User, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { format, parseISO } from 'date-fns';
import { formatPhone } from '../../utils/phoneFormatter';

const TYPE_ICON = {
  call:           <Phone size={13} />,
  order_created:  <ShoppingBag size={13} />,
  order_status:   <ShoppingBag size={13} />,
  order_paid:     <CheckCircle2 size={13} />,
  status_changed: <Tag size={13} />,
};

const TYPE_COLOR = {
  call:           'text-blue-500',
  order_created:  'text-emerald-500',
  order_status:   'text-amber-500',
  order_paid:     'text-emerald-600',
  status_changed: 'text-purple-500',
};

export default function CallHistoryModal({ call, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (call.customer_id) {
      api.get(`/customers/${call.customer_id}`)
        .then((r) => setData({ type: 'customer', customer: r.data, activity: r.data.activity }))
        .catch(() => setData({ type: 'error' }))
        .finally(() => setLoading(false));
    } else {
      api.get('/calls', { params: { search: call.phone, limit: 100, sort_by: 'called_at', sort_dir: 'desc' } })
        .then((r) => setData({ type: 'unknown', calls: r.data.data }))
        .catch(() => setData({ type: 'error' }))
        .finally(() => setLoading(false));
    }
  }, [call.customer_id, call.phone]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">
              {call.customer_id ? 'היסטוריית התקשרויות' : `שיחות ממספר ${formatPhone(call.phone)}`}
            </h2>
            {data?.type === 'customer' && data.customer && (
              <button
                onClick={() => { onClose(); navigate(`/customers/${call.customer_id}`); }}
                className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline mt-0.5"
              >
                <User size={11} />
                {data.customer.name}
                <ExternalLink size={10} />
              </button>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: `${80 - i * 8}%` }} />
              ))}
            </div>
          ) : data?.type === 'error' ? (
            <p className="text-sm text-red-500 text-center py-8">שגיאה בטעינת הנתונים</p>
          ) : data?.type === 'customer' ? (
            <ActivityList activity={data.activity} />
          ) : (
            <CallsList calls={data.calls} currentCallId={call.id} />
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityList({ activity }) {
  if (!activity || activity.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-8">אין פעילות רשומה</p>;
  }

  return (
    <div className="space-y-1">
      {activity.map((entry) => (
        <div key={entry.id} className="flex items-start gap-3 py-2.5 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
          <span className={`mt-0.5 shrink-0 ${TYPE_COLOR[entry.type] || 'text-gray-400'}`}>
            {TYPE_ICON[entry.type] || <MessageSquare size={13} />}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{entry.description}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {format(parseISO(entry.created_at), 'dd/MM/yyyy HH:mm')}
              {entry.performed_by_name && ` · ${entry.performed_by_name}`}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function CallsList({ calls, currentCallId }) {
  if (!calls || calls.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-8">אין שיחות רשומות</p>;
  }

  return (
    <div className="space-y-1">
      {calls.map((c) => (
        <div
          key={c.id}
          className={`flex items-start gap-3 py-2.5 border-b border-gray-100 dark:border-gray-700/50 last:border-0 ${c.id === currentCallId ? 'bg-primary-50/50 dark:bg-primary-900/10 -mx-6 px-6' : ''}`}
        >
          <span className="mt-0.5 shrink-0 text-blue-500"><Phone size={13} /></span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-800 dark:text-gray-200">
              {format(parseISO(c.called_at), 'dd/MM/yyyy HH:mm')}
              {c.id === currentCallId && <span className="mr-2 text-xs text-primary-500 font-medium">← שיחה זו</span>}
            </p>
            {c.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{c.notes}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
